
import {map,  mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, EMPTY, of, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SocoConfigurationService, SocoHttpService, ApiResult } from '@app/base/soco';
import { EnvironmentService } from '@app/core/services/environment.service';
import { Account, APIInfo, User } from '@app/core/models/user';
import { CompanyDetails } from '@app/core/models/app/company-details.model';
import { LookupsService } from '@app/core/services/lookups.service';
import { SessionService } from '@app/core/services/session.service';
import { ConstantsService } from '@app/core/services/constants.service';

/* istanbul ignore next */
@Injectable()
export class AccountService {
  public account: Account;
  private tokenLoaded: boolean;
  private anonymousTokenLoaded: boolean;

  constructor(
    private ngHttp: HttpClient,
    private socoHttp: SocoHttpService,
    private config: SocoConfigurationService,
    private environment: EnvironmentService,
    private constantsSvc: ConstantsService,
    private lookupSvc: LookupsService,
    private sessionSvc: SessionService
  ) {
    this.account = new Account();
    this.tokenLoaded = false;
    this.anonymousTokenLoaded = false;
  }

  public isAuthenticated(destUrl: string): Observable<any> {
    return this.ngHttp.get<ApiResult<APIInfo>>('Account/GetApiInfo').pipe(map(result => {
      // Successfully casted ApiInfo, mean user is authenticated and token acquired
      this.tokenLoaded = (result.data.webApiToken && result.data.webApiToken !== '' && result.data.webApiToken !== 'ANONYMOUS');

      if (result.data.role == null)      {
        const url =
        '/' +
        (this.environment.isSoCoEnv
          ? 'account/accessdenied'
          : this.constantsSvc.localIISAppName + '/account/accessdenied');
          window.location.href = url;
        }
      return this.tokenLoaded;
    })).pipe(
      catchError(response => {
      // If user is unauthenticated,call will not end with 401,instead ends with a redirect to Account/Logon and return the page content.
      // If no JSON object that match ApiResult<APIInfo> the get method will fail, which mean route to login
      this.routeToLogin(destUrl);
      return of(false);
    }));
  }

  public resolveBuilderAccountInfo(): Observable<any> {
    const env = this.environment.appEnvironmentName.toLowerCase();

    return this.ngHttp.get<ApiResult<APIInfo>>('Account/GetApiInfo').pipe(
      mergeMap((res: any) => {
        const { webApiBaseUrl } = res.data; // Must manually set API, as config settings are not defined for forkJoin below.
        this.setApiInfo(res.data);

        return forkJoin([
          this.ngHttp.get<ApiResult<CompanyDetails>>( `${webApiBaseUrl}/Common/GetLdcCollection`).pipe(map((companiesResult) => {
            this.lookupSvc.companies = companiesResult.data;
            this.sessionSvc.activeLdc = this.lookupSvc.companies[this.sessionSvc.paramLdc];
            this.sessionSvc.sourceToContract = this.constantsSvc.BuilderUser;

            // TODO: DSS: created an observable to support resetting values after webauth redirect (builder-shell.component.ts) - BT 3/21/24
            this.sessionSvc.GetActiveLdc(this.lookupSvc.companies[this.sessionSvc.paramLdc]);

            // Return an empty observable, as there no other consequent calls to API
            return EMPTY;
          })),
          this.socoHttp.get<ApiResult<User>>({ url: 'Account' }),
          this.ngHttp.get<ApiResult<string[]>>(`${webApiBaseUrl}/Settings`).pipe(map((result: any) => {
            this.config.settings = result.data;
          }))
        ]);
      }));
  }

  public resolveAnonymousAccountInfo(): Observable<any> {
    // If we retrieved a token (will happen when builder login) or anonymous-token (when applicant land) don't retrieve it again.
    if (this.tokenLoaded || this.anonymousTokenLoaded) { return EMPTY; }

      //return this.socoHttp.get<ApiResult<APIInfo>>({ excludeWebApiBaseUrl:true, url:document.location.href + '/Account/GetAnonymousApiInfo'}).pipe(map((result: any) => {
      return this.ngHttp.get<ApiResult<APIInfo>>( 'Account/GetAnonymousApiInfo').pipe(map(result => {
      this.setApiInfo(result.data);
      this.anonymousTokenLoaded = true;

      return forkJoin([
        this.socoHttp.get<CompanyDetails>({ url: 'Common/GetLdcCollection'}).subscribe( companiesResult => {
          this.lookupSvc.companies = companiesResult.data;
          this.sessionSvc.activeLdc = this.lookupSvc.companies[this.sessionSvc.paramLdc];

          // TODO: DSS: created an observable to support failing applicant bcalookup calls (applicant.component.ts) - BT 3/5/24
          this.sessionSvc.GetActiveLdc(this.lookupSvc.companies[this.sessionSvc.paramLdc]);

          // Return an empty observable, as there no other consequent calls to API
          return EMPTY;
        }),
        this.socoHttp.get<ApiResult<string[]>>({url: 'Settings'}).subscribe(result => {
          this.config.settings = result.data;
        })
      ]);

    }));
  }

  private routeToLogin(returnUrl: string): void {
    let url = '/account/logon';

    url += '?ReturnUrl=' + returnUrl; // Append return url to the path
    window.location.href = url;
  }

  private setApiInfo(info: APIInfo) {
    this.account.apiInfo = info;
    if (this.account.apiInfo.webApiBaseUrl[this.account.apiInfo.webApiBaseUrl.length - 1] !== '/') {
      this.account.apiInfo.webApiBaseUrl += '/';
    }
    this.config.webApiToken = this.account.apiInfo.webApiToken;
    this.config.webApiBaseUrl = this.account.apiInfo.webApiBaseUrl;
    this.config.coolSecurityToken = this.account.apiInfo.coolSecurityToken;
  }
}
